import React from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import StarsIcon from "../styles/StarsIcon";
import { Button, TableWrap, Table, THead, TBody, Tr, Th, Td } from "./ui";
import { getSelectedPlanName } from "../shared/_helpers/selectedPlan";

const ADMIN_ROLES = ["super-admin", "admin"];

function isAdminRole(role) {
  return ADMIN_ROLES.includes(role);
}

function getUserInitials(item) {
  const firstInitial = item?.firstName ? item.firstName.trim().charAt(0) : "";
  const lastInitial = item?.lastName ? item.lastName.trim().charAt(0) : "";
  return `${firstInitial}${lastInitial}`;
}

// The roster's plan column (#90). It matched only the legacy lookup keys
// ('Platinum price' / 'Essential price'), but current keys are
// `${nickname}-${hex}`, so every plan created since rendered '-'. The shared
// resolver reads the price nickname from either `selected_plan` shape.
// Exported for tests.
export function getSubscriptionTypeLabel(item) {
  return getSelectedPlanName(item?.selected_plan);
}

function formatMemberSince(createdAt) {
  return createdAt ? moment(createdAt).format("Do MMMM YYYY") : "N/A";
}

function TableProfileAvatar({ item }) {
  if (item?.profile_image) {
    return (
      <img
        className="tw-h-10 tw-w-10 tw-shrink-0 tw-rounded-pill tw-object-cover"
        src={`${process.env.REACT_APP_BACKEND}/uploads/${item.profile_image}`}
        alt="Profile"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-pill tw-bg-surface-selected tw-text-sm tw-font-bold tw-text-text"
    >
      {getUserInitials(item)}
    </span>
  );
}

// Name is the one column that always shows; the action columns sit right.
const ACTION_HEADERS = ["Edit", "Block"];

function columnProps(header) {
  if (ACTION_HEADERS.includes(header)) return { align: "right" };
  if (header === "Name" || header === "E-mail") return {};
  return { hideOnPhone: true };
}

function DefaultTableRow({
  item,
  headers,
  role,
  onEditAdvisorClick,
  onBlockUserClick,
  handleUserDetailClick,
}) {
  const canManage = isAdminRole(role);

  return (
    <>
      <Td onClick={() => handleUserDetailClick(item._id)} className="tw-cursor-pointer">
        <div data-slot="user_name" className="tw-flex tw-items-center tw-gap-3">
          <TableProfileAvatar item={item} />
          <div className="tw-min-w-0">
            <span className="tw-font-semibold">
              {item?.firstName} {item?.lastName}
            </span>
          </div>
        </div>
      </Td>
      {headers?.includes("Financial Advisor") && (
        <Td hideOnPhone>
          {item?.advisor?.firstName ? item?.advisor?.firstName : "-"} {item?.advisor?.lastName}
        </Td>
      )}
      {headers?.includes("Subscription Type") && (
        <Td hideOnPhone>{getSubscriptionTypeLabel(item)}</Td>
      )}
      <Td className="tw-break-all">{item?.email}</Td>
      {headers?.includes("Phone Number") && <Td hideOnPhone>{item?.mobile}</Td>}
      {headers?.includes("No.Clients") && <Td hideOnPhone>{item?.uniqueClientCount}</Td>}
      {headers?.includes("Edit") && canManage && (
        <Td align="right">
          <Button
            id="edit-advisor"
            variant="icon"
            className="tw-border-solid"
            size="sm"
            iconOnly
            aria-label="Edit"
            onClick={() => onEditAdvisorClick(item)}
          >
            <StarsIcon name="edit" className="tw-h-4 tw-w-4 tw-shrink-0" />
          </Button>
        </Td>
      )}
      {headers?.includes("Block") && canManage && (
        <Td align="right">
          <Button
            id="block-advisor"
            variant="icon"
            className="tw-border-solid"
            size="sm"
            iconOnly
            aria-label={item.status ? "Block" : "Unblock"}
            onClick={() => onBlockUserClick(item)}
          >
            <StarsIcon
              name="ban"
              className={item.status ? "tw-h-4 tw-w-4 tw-shrink-0" : "tw-h-4 tw-w-4 tw-shrink-0 tw-text-danger"}
            />
          </Button>
        </Td>
      )}
      {headers?.includes("Member Since") && (
        <Td hideOnPhone>{formatMemberSince(item.createdAt)}</Td>
      )}
    </>
  );
}

const TableComponent = ({ headers, data, test, onEditAdvisorClick, onBlockUserClick, onBlockAdvisorClick }) => {
  const sortedData = data ? [...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) : [];
  const navigate = useNavigate();
  // SEC-066: the edit/block columns are gated by the server-verified role —
  // this table only ever renders inside a ProtectedRoutes subtree, so
  // verification has already settled by the time it mounts.
  const { role } = useAuth();
  const handleUserDetailClick = (clientId) => {
    test === "temp" && navigate(`/dashboard/${clientId}?client=true`);
  };

  return (
    <TableWrap>
      <Table>
        <THead>
          <Tr className="hover:tw-bg-transparent">
            {headers?.map((header) => (
              <Th key={header} {...columnProps(header)}>{header}</Th>
            ))}
          </Tr>
        </THead>
        <TBody>
          {sortedData?.length > 0 ? (
            sortedData?.map((item) => (
              <Tr key={item._id}>
                <DefaultTableRow
                  item={item}
                  headers={headers}
                  role={role}
                  onEditAdvisorClick={onEditAdvisorClick}
                  onBlockUserClick={onBlockUserClick}
                  handleUserDetailClick={handleUserDetailClick}
                />
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={headers?.length || 1} className="tw-text-center">
                No data found.
              </Td>
            </Tr>
          )}
        </TBody>
      </Table>
    </TableWrap>
  );
};

export default TableComponent;
